import moosiqs from "../../server/data/helpers/moosiqs";
import { NextPage } from "next";
import {
  Grid,
  Container,
  Image,
  Text,
  Skeleton,
  useMantineTheme,
  Button,
  ScrollArea,
} from "@mantine/core";
import { MainLayout } from "../../components/Layouts/MainLayout";
import { createMoosiqsApiClient } from "../../server/services/music/moosiqs";
import config from "../../config";
import Head from "next/head";
import { AppleMusicPlayer } from "../../components/AppleMusic/AppleMusicPlayer";
import { getSiteUrl } from "../../lib/utils/getSiteUrl";
import React from "react";

interface Props {
  album: any;
  user?: any;
  slug: string;
}

const PRIMARY_COL_HEIGHT = 400;

const padLeft = (time) => {
  return time <= 9 ? "0" + time : time;
};

const millisecondsToDuration = (ms) => {
  const seconds = ms / 1000;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds - minutes * 60);

  return minutes + ":" + padLeft(remainingSeconds);
};

const getAmazonSearchLink = (album: string, artist: string) => {
  return `https://www.amazon.co.uk/s?k=${album}+by+${artist}&i=popular&sprefix=${album.toLowerCase()}+by+${artist.toLowerCase()}&linkCode=ll2&tag=moosiqs0a-21&linkId=9b9da080642873d1988fa09c7b0fd0f7&language=en_GB&ref_=as_li_ss_tl`;
};

const Album: NextPage<Props> = ({ album, user, slug }) => {
  const theme = useMantineTheme();
  // @ts-ignore
  const SECONDARY_COL_HEIGHT = PRIMARY_COL_HEIGHT / 2 - theme.spacing.md / 2;
  const child = (
    <Skeleton height={SECONDARY_COL_HEIGHT} radius="md" animate={false} />
  );
  return (
    <MainLayout user={user}>
      <Head>
        <title>{`${album.title} by ${album.artist} - Moosiqs.com`}</title>
        <meta
          name="description"
          content={`Album details for ${album.title} by ${album.artist}`}
        />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link
          rel="alternate"
          href={`${getSiteUrl()}/album/${slug}`}
          hrefLang="en"
        />
      </Head>
      <Container size="lg" px="lg">
        <h1
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {album.title} by {album.artist}
          <Button
            variant="filled"
            color="blue"
            onClick={() => {
              window.open(
                getAmazonSearchLink(album.title, album.artist),
                "_blank"
              );
            }}
          >
            Buy
          </Button>
        </h1>
        <Grid>
          <Grid.Col xs={6}>
            <Image
              src={album?.coverUrl}
              alt={`Album cover for ${album.title} - ${album.artist}`}
            />
          </Grid.Col>
          <Grid.Col xs={6}>
            <ScrollArea h={550}>
              {album?.tracks?.map((track) => (
                <Text key={track.id}>
                  {track.track}. {track.name}
                  <Text style={{ float: "right" }}>
                    {track.duration > 0
                      ? millisecondsToDuration(track.duration)
                      : null}
                  </Text>
                </Text>
              ))}
            </ScrollArea>
          </Grid.Col>
          <Grid.Col xs={12}>
            {album.description && <p>{album.description}</p>}
            {album?.meta?.appleMusic?.attributes?.editorialNotes?.standard && (
              <p>
                {album?.meta?.appleMusic?.attributes.editorialNotes.standard}
              </p>
            )}
          </Grid.Col>
          <Grid.Col xs={12}>
            {album?.meta?.appleMusic && (
              <AppleMusicPlayer url={album?.meta.appleMusic.attributes.url} />
            )}
          </Grid.Col>
        </Grid>
      </Container>
    </MainLayout>
  );
};

const getIdFromAlbumSlug = (slug: string) => {
  return slug.split("-")?.reverse()?.[1];
};

const getProviderIdFromAlbumSlug = (slug: string) => slug.split("-").pop();

export const getServerSideProps = async ({ params }) => {
  if (params?.album) {
    const moosiqsApiClient = createMoosiqsApiClient(config.moosiqsApi);
    const providerId = getProviderIdFromAlbumSlug(params.album);
    const albumId = getIdFromAlbumSlug(params.album);
    const album = await moosiqs.music.getAlbum(
      moosiqsApiClient,
      Number(providerId),
      Number(albumId)
    );

    if (album) {
      return { props: { album, slug: params.album } };
    }

    await moosiqs.music.addMusic(
      moosiqsApiClient,
      Number(providerId),
      Number(albumId)
    );

    return {
      redirect: {
        destination: `/album/${params.album}`,
        permanent: false,
      },
    };
  }

  return {
    notFound: true,
  };
};
export default Album;
